/**
 * Help Center content — the single source of truth for the in-app `/help`
 * page (see `src/features/help/HelpPage.tsx`).
 *
 * Each `HelpEntry` documents one screen / sub-feature of the app, bilingually
 * (EN + ES), answering three questions: what it does, what data it shows, and
 * what it is for. The Help Center groups entries by `category`, supports
 * keyword search, and cross-links via `related`.
 *
 * ⚠️  KEEP THIS IN SYNC. Whenever a user-facing screen changes or is added,
 * update (or add) its entry here in BOTH languages — see
 * `.claude/rules/help-sync.md`.
 */

export type HelpCategory =
  | 'getting-started'
  | 'dashboard'
  | 'events'
  | 'stations'
  | 'stock'
  | 'surveys'
  | 'contacts'
  | 'operator'
  | 'workspace'
  | 'billing';

/** A single bilingual string pair. */
interface Bilingual {
  en: string;
  es: string;
}

export interface HelpEntry {
  /** Stable slug — also the `#hash` anchor used by contextual deep-links. */
  id: string;
  category: HelpCategory;
  /** Static app path this screen lives at, when one exists ("Go to page"). */
  route?: string;
  title: Bilingual;
  /** Qué hace — what the screen does. */
  whatItDoes: Bilingual;
  /** Qué datos lleva — what data it shows / captures. */
  whatData: Bilingual;
  /** Para qué sirve — what it is for / why it matters. */
  whatFor: Bilingual;
  /** Search keywords, per language. */
  keywords: { en: string[]; es: string[] };
  /** Ids of related entries (references / cross-links). */
  related: string[];
}

export const HELP_CATEGORIES: { id: HelpCategory; label: Bilingual }[] = [
  { id: 'getting-started', label: { en: 'Getting started', es: 'Primeros pasos' } },
  { id: 'dashboard', label: { en: 'Dashboard & brands', es: 'Inicio y marcas' } },
  { id: 'events', label: { en: 'Projects & events', es: 'Proyectos y eventos' } },
  { id: 'stations', label: { en: 'Stations', es: 'Estaciones' } },
  { id: 'stock', label: { en: 'Stock', es: 'Stock' } },
  { id: 'surveys', label: { en: 'Journeys', es: 'Journeys' } },
  { id: 'contacts', label: { en: 'Contacts & insights', es: 'Contactos e insights' } },
  { id: 'operator', label: { en: 'Operator & redemption', es: 'Operador y canje' } },
  { id: 'workspace', label: { en: 'Workspace & settings', es: 'Espacio de trabajo' } },
  { id: 'billing', label: { en: 'Billing & plan', es: 'Facturación y plan' } },
];

export const HELP_ENTRIES: HelpEntry[] = [
  // ── Getting started ──────────────────────────────────────────────────────
  {
    id: 'overview',
    category: 'getting-started',
    title: { en: 'What is Xotski', es: 'Qué es Xotski' },
    whatItDoes: {
      en: 'Xotski lets a brand run engagement activations at events: a booth with a QR code, a journey, a spin-the-wheel game and physical prizes.',
      es: 'Xotski permite a una marca montar activaciones en eventos: un booth con código QR, una journey, una ruleta y premios físicos.',
    },
    whatData: {
      en: 'Your workspace is organised as Brands → Projects → Events → Stations, with stock (merch/prizes), journeys and the people who respond (contacts).',
      es: 'Tu espacio se organiza como Marcas → Proyectos → Eventos → Estaciones, con stock (merch/premios), journeys y las personas que responden (contactos).',
    },
    whatFor: {
      en: 'It turns a live event into measurable results: who attended, what they answered, what prizes were handed out and how each event performed.',
      es: 'Convierte un evento en vivo en resultados medibles: quién asistió, qué respondió, qué premios se entregaron y cómo rindió cada evento.',
    },
    keywords: {
      en: ['overview', 'intro', 'what is', 'start', 'xotski', 'basics'],
      es: ['resumen', 'introducción', 'qué es', 'empezar', 'xotski', 'básico'],
    },
    related: ['navigation', 'onboarding', 'dashboard'],
  },
  {
    id: 'navigation',
    category: 'getting-started',
    title: { en: 'Navigating the app', es: 'Navegar por la app' },
    whatItDoes: {
      en: 'On desktop the left sidebar holds the main areas; on mobile a fixed bottom bar (Home · My xotskies · Insights · More) replaces it. The top bar has search, notifications and this Help button.',
      es: 'En escritorio la barra lateral izquierda tiene las áreas principales; en móvil una barra inferior fija (Inicio · My xotskies · Insights · Más) la reemplaza. La barra superior tiene búsqueda, notificaciones y este botón de Ayuda.',
    },
    whatData: {
      en: 'The sidebar shows top-level destinations; everything workspace-related (billing, access, notifications, integrations…) lives inside the Workspace page.',
      es: 'La barra lateral muestra los destinos principales; todo lo del espacio de trabajo (facturación, accesos, notificaciones, integraciones…) vive dentro de la página Workspace.',
    },
    whatFor: {
      en: 'Knowing where things live saves time — most screens are two clicks from Home.',
      es: 'Saber dónde está cada cosa ahorra tiempo — casi todo está a dos clics de Inicio.',
    },
    keywords: {
      en: ['navigation', 'menu', 'sidebar', 'bottom bar', 'mobile', 'workspace'],
      es: ['navegación', 'menú', 'barra lateral', 'barra inferior', 'móvil', 'workspace'],
    },
    related: ['overview', 'settings-overview'],
  },
  {
    id: 'onboarding',
    category: 'getting-started',
    route: '/onboarding/brand-profile',
    title: { en: 'First-time setup', es: 'Configuración inicial' },
    whatItDoes: {
      en: 'New accounts are guided through a brand profile and a brand kit (logo, colours) before reaching the dashboard, then a wizard helps create the first event.',
      es: 'Las cuentas nuevas pasan por un perfil de marca y un brand kit (logo, colores) antes de llegar al inicio, y luego un asistente ayuda a crear el primer evento.',
    },
    whatData: {
      en: 'Brand name, industry, company type, logo and brand colours; the first-steps checklist tracks brand, event and station creation.',
      es: 'Nombre de marca, industria, tipo de compañía, logo y colores; la lista de primeros pasos rastrea la creación de marca, evento y estación.',
    },
    whatFor: {
      en: 'It gets a new workspace ready to run its first activation without guesswork.',
      es: 'Deja un espacio nuevo listo para correr su primera activación sin adivinar.',
    },
    keywords: {
      en: ['onboarding', 'setup', 'first event', 'wizard', 'brand kit', 'checklist'],
      es: ['onboarding', 'configuración', 'primer evento', 'asistente', 'brand kit', 'checklist'],
    },
    related: ['brand-identity', 'dashboard', 'event-detail'],
  },

  // ── Dashboard & brands ───────────────────────────────────────────────────
  {
    id: 'dashboard',
    category: 'dashboard',
    route: '/',
    title: { en: 'Home dashboard', es: 'Panel de inicio' },
    whatItDoes: {
      en: 'The landing screen — shows your workspace at a glance: sub-brands, recent activity and quick KPIs.',
      es: 'La pantalla de aterrizaje — muestra tu espacio de un vistazo: sub-marcas, actividad reciente y KPIs rápidos.',
    },
    whatData: {
      en: 'Live activations count, spend, people reached, the list of sub-brands and the brand managers assigned.',
      es: 'Cantidad de activaciones en vivo, gasto, personas alcanzadas, la lista de sub-marcas y los gestores asignados.',
    },
    whatFor: {
      en: 'A single starting point to jump into any brand, project or event.',
      es: 'Un único punto de partida para entrar a cualquier marca, proyecto o evento.',
    },
    keywords: {
      en: ['home', 'dashboard', 'portfolio', 'kpi', 'landing', 'overview'],
      es: ['inicio', 'panel', 'portafolio', 'kpi', 'aterrizaje', 'resumen'],
    },
    related: ['brand-detail', 'insights', 'onboarding'],
  },
  {
    id: 'brand-detail',
    category: 'dashboard',
    route: '/brand',
    title: { en: 'Brand home', es: 'Inicio de marca' },
    whatItDoes: {
      en: 'The home for one brand — a focused landing surface: an Atledi greeting, three quick-create cards (Add Xotskies, Add events, Add a Project) and a "jump back into your projects" list. The full project and xotski catalogs live on their own sidebar pages.',
      es: 'El inicio de una marca — una pantalla enfocada: un saludo de Atledi, tres tarjetas de creación rápida (Agregar Xotskies, Agregar eventos, Agregar un proyecto) y una lista para "retomar tus proyectos". Los catálogos completos de proyectos y xotskies viven en sus propias páginas del sidebar.',
    },
    whatData: {
      en: 'A live greeting, your brand projects (name, events, stations and status) and an Atledi composer. Creating a project opens its form in the right-side panel instead of a pop-up dialog.',
      es: 'Un saludo en vivo, los proyectos de tu marca (nombre, eventos, estaciones y estado) y un composer de Atledi. Crear un proyecto abre su formulario en el panel derecho en vez de un diálogo emergente.',
    },
    whatFor: {
      en: 'A calm starting point to greet the brand, jump into a recent project, or quickly create the next one.',
      es: 'Un punto de partida tranquilo para saludar a la marca, entrar a un proyecto reciente o crear el siguiente rápidamente.',
    },
    keywords: {
      en: ['brand', 'home', 'projects', 'atledi', 'create', 'jump back', 'quick actions'],
      es: ['marca', 'inicio', 'proyectos', 'atledi', 'crear', 'retomar', 'acciones rápidas'],
    },
    related: ['dashboard', 'projects', 'brand-identity', 'trends', 'workspace-switcher'],
  },
  {
    id: 'workspace-switcher',
    category: 'workspace',
    title: { en: 'Workspace brand switcher', es: 'Selector de marca del workspace' },
    whatItDoes: {
      en: 'The pill on the left of the top bar — picks the brand that scopes the whole app.',
      es: 'El pill a la izquierda del topbar — elige la marca que define el contexto de toda la app.',
    },
    whatData: {
      en: 'The parent company on the left of the › and the brand you are currently working in on the right. Click to open a searchable list of brands you have access to.',
      es: 'La empresa padre a la izquierda del › y la marca en la que estás trabajando a la derecha. Haz clic para abrir una lista de marcas a las que tienes acceso, con búsqueda.',
    },
    whatFor: {
      en: 'So projects, events, stations, stock and favorites all filter to one brand at a time — and you never see another brand’s data by accident.',
      es: 'Para que proyectos, eventos, estaciones, stock y favoritos filtren por una sola marca a la vez — y nunca veas datos de otra marca por accidente.',
    },
    keywords: {
      en: ['workspace', 'brand', 'switcher', 'topbar', 'pill', 'context', 'select'],
      es: ['workspace', 'marca', 'selector', 'topbar', 'pill', 'contexto', 'elegir'],
    },
    related: ['brand-detail', 'dashboard'],
  },
  {
    id: 'brand-identity',
    category: 'dashboard',
    route: '/brand-identity',
    title: { en: 'Brand identity', es: 'Identidad de marca' },
    whatItDoes: {
      en: 'Edits the workspace brand kit — logo, primary and accent colours, fonts.',
      es: 'Edita el brand kit del espacio — logo, colores primario y de acento, tipografías.',
    },
    whatData: {
      en: 'Logo image, colour values and typography that brand the journeys consumers see.',
      es: 'Imagen del logo, valores de color y tipografías que dan identidad a las journeys que ve el consumidor.',
    },
    whatFor: {
      en: 'So every public journey and prize screen looks like the brand, not generic.',
      es: 'Para que cada journey pública y pantalla de premio se vea como la marca, no genérica.',
    },
    keywords: {
      en: ['brand kit', 'logo', 'colors', 'identity', 'theme', 'fonts'],
      es: ['brand kit', 'logo', 'colores', 'identidad', 'tema', 'tipografía'],
    },
    related: ['brand-detail', 'public-survey-runner', 'onboarding'],
  },
  {
    id: 'agency',
    category: 'dashboard',
    title: { en: 'Agency hub', es: 'Hub de agencia' },
    whatItDoes: {
      en: 'For agency accounts — shows every client brand the agency manages, plus team and billing context.',
      es: 'Para cuentas de agencia — muestra cada marca cliente que la agencia gestiona, más equipo y facturación.',
    },
    whatData: {
      en: 'The list of managed brands, aggregate activity and the agency workspace identity.',
      es: 'La lista de marcas gestionadas, actividad agregada y la identidad del espacio de la agencia.',
    },
    whatFor: {
      en: 'To let an agency oversee many client brands from one workspace.',
      es: 'Para que una agencia supervise muchas marcas cliente desde un solo espacio.',
    },
    keywords: {
      en: ['agency', 'clients', 'brands', 'multi-brand'],
      es: ['agencia', 'clientes', 'marcas', 'multi-marca'],
    },
    related: ['brand-detail', 'access-manager'],
  },

  // ── Projects & events ────────────────────────────────────────────────────
  {
    id: 'projects',
    category: 'events',
    title: { en: 'Project workspace', es: 'Espacio del proyecto' },
    whatItDoes: {
      en: 'A project groups related events under one campaign and opens in a workspace with three tabs. PLAN is a drag-and-drop planner: drag xotskis, reporters or stations onto an event, batch-create several events at once, and edit an event\'s properties inline. TRACK groups events by phase (live / upcoming / in edit / completed). MEASURE shows project-wide stats and charts.',
      es: 'Un proyecto agrupa eventos relacionados bajo una campaña y abre en un espacio con tres pestañas. PLAN es un planificador con arrastrar y soltar: arrastra xotskis, reporteros o estaciones a un evento, crea varios eventos a la vez y edita las propiedades de un evento ahí mismo. TRACK agrupa los eventos por fase (en vivo / próximos / en edición / completados). MEASURE muestra estadísticas y gráficas de todo el proyecto.',
    },
    whatData: {
      en: 'Project name, code reference, an authoritative start/end date range (picked by calendar day — no time) that limits the dates allowed on its events, status, and its child events. Events themselves are scheduled with an exact date and time inside that range. Dragging a xotski onto an event allocates it to all that event\'s stations; every event is created with one station.',
      es: 'Nombre del proyecto, código, un rango de fechas autoritativo (se elige por día de calendario — sin hora) que limita las fechas permitidas en sus eventos, estado y sus eventos hijos. Los eventos se programan con fecha y hora exactas dentro de ese rango. Arrastrar un xotski a un evento lo asigna a todas las estaciones de ese evento; cada evento nace con una estación.',
    },
    whatFor: {
      en: 'To plan, track and measure a multi-event campaign from one place, while the full per-event configuration still lives one click away on the event detail screen.',
      es: 'Para planificar, seguir y medir una campaña de varios eventos desde un solo lugar, mientras la configuración completa de cada evento sigue a un clic en la pantalla de detalle del evento.',
    },
    keywords: {
      en: ['project', 'campaign', 'event group', 'events', 'plan', 'track', 'measure', 'planner', 'drag', 'batch', 'date range'],
      es: ['proyecto', 'campaña', 'grupo de eventos', 'eventos', 'planificar', 'seguir', 'medir', 'planner', 'arrastrar', 'lote', 'rango de fechas'],
    },
    related: ['event-detail', 'trends', 'brand-detail', 'stations', 'stock'],
  },
  {
    id: 'event-detail',
    category: 'events',
    title: { en: 'Event detail', es: 'Detalle de evento' },
    whatItDoes: {
      en: 'The control centre for a single event — configure its stations, journeys, goals and review live and post-event data.',
      es: 'El centro de control de un evento — configura sus estaciones, journeys, metas y revisa datos en vivo y posteriores.',
    },
    whatData: {
      en: 'Event name, address, start/end date and time (must fall within the parent project\'s date range), sections, and tabs for stations, journey config, goals, the live monitor and the report. In the PLAN tab each station expands into an inline stock-allocation editor: tune each xotski slice\'s quantity, duplicate a slice, remove it, and drag to reorder (the order is the wheel display order).',
      es: 'Nombre del evento, dirección, fecha y hora de inicio/fin (deben caer dentro del rango de fechas del proyecto padre), secciones, y pestañas para estaciones, configuración de journey, metas, monitor en vivo y reporte. En la pestaña PLAN cada estación se expande en un editor de allocations en línea: ajusta la cantidad de cada slice de xotski, duplícalo, quítalo y arrástralo para reordenar (el orden es el de la ruleta).',
    },
    whatFor: {
      en: 'It is where an event is set up before it runs and reviewed after — including allocating prizes to each station\'s wheel without leaving the page.',
      es: 'Es donde se prepara un evento antes de correr y se revisa después — incluido asignar premios a la ruleta de cada estación sin salir de la página.',
    },
    keywords: {
      en: ['event', 'detail', 'configure', 'setup', 'tabs'],
      es: ['evento', 'detalle', 'configurar', 'preparar', 'pestañas'],
    },
    related: ['event-stations', 'event-surveys', 'event-goals', 'event-report'],
  },
  {
    id: 'event-stations',
    category: 'events',
    title: { en: 'Event stations tab', es: 'Pestaña de estaciones del evento' },
    whatItDoes: {
      en: 'Adds and arranges the stations (booths) that belong to an event.',
      es: 'Agrega y organiza las estaciones (booths) que pertenecen a un evento.',
    },
    whatData: {
      en: 'Station names, the journey assigned to each, and the stock allocated per station.',
      es: 'Nombres de estación, la journey asignada a cada una y el stock asignado por estación.',
    },
    whatFor: {
      en: 'Each station becomes a physical QR point at the venue where consumers play.',
      es: 'Cada estación se vuelve un punto QR físico en el venue donde el consumidor juega.',
    },
    keywords: {
      en: ['stations', 'booths', 'event', 'assign journey'],
      es: ['estaciones', 'booths', 'evento', 'asignar journey'],
    },
    related: ['event-detail', 'stations-list', 'station-detail'],
  },
  {
    id: 'event-surveys',
    category: 'events',
    title: { en: 'Event journey configuration', es: 'Configuración de journey del evento' },
    whatItDoes: {
      en: 'Picks the journey for the event and customises its experience — hidden questions, age gate, branding.',
      es: 'Elige la journey del evento y personaliza su experiencia — preguntas ocultas, control de edad, branding.',
    },
    whatData: {
      en: 'The selected journey, per-event customisation and the public link / short URL to share.',
      es: 'La journey seleccionada, la personalización por evento y el enlace público / URL corta para compartir.',
    },
    whatFor: {
      en: 'To reuse one journey across events while tuning the experience per event.',
      es: 'Para reutilizar una journey en varios eventos ajustando la experiencia por evento.',
    },
    keywords: {
      en: ['journey config', 'event journey', 'public link', 'short url', 'customize'],
      es: ['configuración journey', 'journey evento', 'enlace público', 'url corta', 'personalizar'],
    },
    related: ['event-detail', 'survey-builder', 'public-survey-runner'],
  },
  {
    id: 'event-goals',
    category: 'events',
    title: { en: 'Event goals', es: 'Metas del evento' },
    whatItDoes: {
      en: 'Sets target numbers for an event (e.g. responses or prizes handed out) and tracks progress toward them.',
      es: 'Fija números objetivo para un evento (ej. respuestas o premios entregados) y rastrea el avance hacia ellos.',
    },
    whatData: {
      en: 'A goal per metric and the live progress percentage against it.',
      es: 'Una meta por métrica y el porcentaje de avance en vivo contra ella.',
    },
    whatFor: {
      en: 'To know during and after the event whether it hit its targets.',
      es: 'Para saber durante y después del evento si alcanzó sus objetivos.',
    },
    keywords: {
      en: ['goals', 'targets', 'progress', 'kpi'],
      es: ['metas', 'objetivos', 'avance', 'kpi'],
    },
    related: ['event-detail', 'event-live-monitor', 'event-report'],
  },
  {
    id: 'event-live-monitor',
    category: 'events',
    title: { en: 'Live event monitor', es: 'Monitor en vivo del evento' },
    whatItDoes: {
      en: 'A real-time view of an event as it runs, with a fullscreen TV mode for the venue.',
      es: 'Una vista en tiempo real del evento mientras corre, con un modo TV a pantalla completa para el venue.',
    },
    whatData: {
      en: 'Live response counts, stock remaining, goal progress and threshold alerts (low stock, goal met).',
      es: 'Conteo de respuestas en vivo, stock restante, avance de metas y alertas por umbral (stock bajo, meta cumplida).',
    },
    whatFor: {
      en: 'To watch an event live and react before stock runs out.',
      es: 'Para vigilar el evento en vivo y reaccionar antes de quedarse sin stock.',
    },
    keywords: {
      en: ['live', 'monitor', 'tv mode', 'real time', 'alerts'],
      es: ['en vivo', 'monitor', 'modo tv', 'tiempo real', 'alertas'],
    },
    related: ['event-detail', 'event-goals', 'plan-limits'],
  },
  {
    id: 'event-report',
    category: 'events',
    title: { en: 'Event report', es: 'Reporte de evento' },
    whatItDoes: {
      en: 'A post-event summary of results, and a revocable public link to share it with a client.',
      es: 'Un resumen de resultados posterior al evento, y un enlace público revocable para compartirlo con un cliente.',
    },
    whatData: {
      en: 'Aggregated response trends and redemption stats — no raw personal data on the public link.',
      es: 'Tendencias de respuesta agregadas y estadísticas de canje — sin datos personales en el enlace público.',
    },
    whatFor: {
      en: 'To prove the event’s results to the brand or client.',
      es: 'Para demostrar los resultados del evento a la marca o cliente.',
    },
    keywords: {
      en: ['report', 'results', 'share link', 'public report', 'pdf'],
      es: ['reporte', 'resultados', 'enlace compartido', 'reporte público', 'pdf'],
    },
    related: ['event-detail', 'event-goals', 'trends', 'insights'],
  },
  {
    id: 'trends',
    category: 'events',
    route: '/trends',
    title: { en: 'Event comparison & trends', es: 'Comparación y tendencias' },
    whatItDoes: {
      en: 'Compares several events side by side and shows trends over time across a project or brand.',
      es: 'Compara varios eventos lado a lado y muestra tendencias en el tiempo por proyecto o marca.',
    },
    whatData: {
      en: 'Per-event metrics (responses, redemptions, engagement) plotted together for comparison.',
      es: 'Métricas por evento (respuestas, canjes, interacción) graficadas juntas para comparar.',
    },
    whatFor: {
      en: 'To see whether activations are improving and which performed best.',
      es: 'Para ver si las activaciones mejoran y cuáles rindieron mejor.',
    },
    keywords: {
      en: ['trends', 'comparison', 'compare events', 'analytics'],
      es: ['tendencias', 'comparación', 'comparar eventos', 'analítica'],
    },
    related: ['event-report', 'insights', 'projects'],
  },

  // ── Stations ─────────────────────────────────────────────────────────────
  {
    id: 'stations-list',
    category: 'stations',
    route: '/stations',
    title: { en: 'Stations list', es: 'Lista de estaciones' },
    whatItDoes: {
      en: 'Lists every station (booth) across the workspace.',
      es: 'Lista todas las estaciones (booths) del espacio de trabajo.',
    },
    whatData: {
      en: 'Station name, the event it belongs to and its assigned journey.',
      es: 'Nombre de estación, el evento al que pertenece y su journey asignada.',
    },
    whatFor: {
      en: 'A quick way to find and open any station.',
      es: 'Una forma rápida de encontrar y abrir cualquier estación.',
    },
    keywords: {
      en: ['stations', 'booths', 'list'],
      es: ['estaciones', 'booths', 'lista'],
    },
    related: ['station-detail', 'event-stations'],
  },
  {
    id: 'station-detail',
    category: 'stations',
    title: { en: 'Station detail', es: 'Detalle de estación' },
    whatItDoes: {
      en: 'Configures a single station — its name, the journey shown, the stock it hands out and operator access.',
      es: 'Configura una estación — su nombre, la journey que muestra, el stock que entrega y el acceso de operador.',
    },
    whatData: {
      en: 'Station settings, stock allocation, the QR / short link and minted operator-access tokens. The same prize can be allocated more than once (each row is a separate wheel slice with its own rating) so a single type can take several slices and win more often — limited by the brand pool still available.',
      es: 'Ajustes de la estación, asignación de stock, el QR / enlace corto y tokens de acceso de operador generados. Un mismo premio puede asignarse más de una vez (cada fila es una porción de ruleta con su propio rating) para que un solo tipo ocupe varias porciones y se gane más seguido — limitado por el pool de marca aún disponible.',
    },
    whatFor: {
      en: 'It defines exactly what a consumer sees and wins at that physical point.',
      es: 'Define exactamente qué ve y gana un consumidor en ese punto físico.',
    },
    keywords: {
      en: ['station', 'detail', 'qr', 'operator link', 'allocation', 'probability', 'wheel slices'],
      es: ['estación', 'detalle', 'qr', 'enlace operador', 'asignación', 'probabilidad', 'porciones ruleta'],
    },
    related: ['stations-list', 'station-qr', 'operator-station', 'stock-list'],
  },
  {
    id: 'station-qr',
    category: 'stations',
    title: { en: 'Station QR print', es: 'Impresión de QR de estación' },
    whatItDoes: {
      en: 'Generates a printable QR code for a station.',
      es: 'Genera un código QR imprimible para una estación.',
    },
    whatData: {
      en: 'The QR encodes the station’s public short link to the journey.',
      es: 'El QR codifica el enlace corto público de la estación a la journey.',
    },
    whatFor: {
      en: 'Print it and place it on the booth so consumers can scan and play.',
      es: 'Imprímelo y colócalo en el booth para que el consumidor escanee y juegue.',
    },
    keywords: {
      en: ['qr', 'print', 'short link', 'booth'],
      es: ['qr', 'imprimir', 'enlace corto', 'booth'],
    },
    related: ['station-detail', 'public-survey-runner'],
  },

  // ── Stock ────────────────────────────────────────────────────────────────
  {
    id: 'stock-list',
    category: 'stock',
    route: '/stock',
    title: { en: 'My xotskies (stock)', es: 'My xotskies (stock)' },
    whatItDoes: {
      en: 'Manages your inventory — the merch and prizes that appear on the spin wheels and printables.',
      es: 'Gestiona tu inventario — el merch y los premios que aparecen en las ruletas e impresos.',
    },
    whatData: {
      en: 'Each item’s name, image, classification, wheel rating (1–5), quantity and low-stock threshold.',
      es: 'Nombre de cada artículo, imagen, clasificación, rating de ruleta (1–5), cantidad y umbral de stock bajo.',
    },
    whatFor: {
      en: 'It feeds the prize wheel; the rating controls how often each prize is won.',
      es: 'Alimenta la ruleta de premios; el rating controla con qué frecuencia se gana cada premio.',
    },
    keywords: {
      en: ['stock', 'inventory', 'merch', 'prizes', 'xotskies', 'wheel'],
      es: ['stock', 'inventario', 'merch', 'premios', 'xotskies', 'ruleta'],
    },
    related: ['station-detail', 'event-stations', 'redeem', 'brand-inventory'],
  },
  {
    id: 'brand-inventory',
    category: 'stock',
    route: '/brand-inventory',
    title: { en: 'Brand inventory', es: 'Inventario de marca' },
    whatItDoes: {
      en: 'Shows the brand-level pool of items — the upstream warehouse you allocate into projects.',
      es: 'Muestra el pool de artículos a nivel de marca — el almacén central que se asigna a los proyectos.',
    },
    whatData: {
      en: 'Each item’s name, image, quantity, lifecycle state (unassigned, allocated, distributed, claimed, returned, written off), rarity rating and tags (event affinity, demographic, language, season, size).',
      es: 'Nombre, imagen, cantidad, estado de ciclo de vida (sin asignar, asignado, distribuido, reclamado, devuelto, dado de baja), rareza y etiquetas (afinidad de evento, demografía, idioma, temporada, talla) por artículo.',
    },
    whatFor: {
      en: 'It is the central source from which a project’s "My xotskies" warehouse pulls. Leftovers return here on event close, and items can be written off with an auditable reason.',
      es: 'Es la fuente central desde la cual el almacén "Mis xotskies" del proyecto extrae. Los sobrantes regresan al cerrar un evento, y los artículos pueden darse de baja con una razón auditada.',
    },
    keywords: {
      en: ['brand inventory', 'warehouse', 'pool', 'allocate', 'tags', 'lifecycle', 'write-off'],
      es: ['inventario de marca', 'almacén', 'pool', 'asignar', 'etiquetas', 'ciclo de vida', 'baja'],
    },
    related: ['stock-list', 'station-detail', 'redeem'],
  },
  {
    id: 'settings-brand',
    category: 'stock',
    route: '/settings/brand',
    title: { en: 'Brand settings', es: 'Ajustes de marca' },
    whatItDoes: {
      en: 'Per-brand toggles that affect how the brand experience runs across Xotski. Today it surfaces the Brand Inventory opt-in.',
      es: 'Toggles por marca que afectan cómo corre la experiencia de la marca en Xotski. Hoy expone el opt-in del Inventario de marca.',
    },
    whatData: {
      en: 'A list of every brand visible to your workspace with a switch for Brand Inventory (PRD §9).',
      es: 'Lista de cada marca visible para tu espacio con un switch para Inventario de marca (PRD §9).',
    },
    whatFor: {
      en: 'Use it to turn on Brand Inventory for a specific brand. Without it the /brand-inventory endpoints refuse to serve.',
      es: 'Úsalo para activar el Inventario de marca en una marca específica. Sin él, los endpoints /brand-inventory se niegan a responder.',
    },
    keywords: {
      en: ['brand settings', 'opt-in', 'toggle', 'brand inventory'],
      es: ['ajustes de marca', 'opt-in', 'switch', 'inventario de marca'],
    },
    related: ['brand-inventory', 'stock-list'],
  },

  // ── Surveys ──────────────────────────────────────────────────────────────
  {
    id: 'survey-builder',
    category: 'surveys',
    route: '/surveys/builder',
    title: { en: 'Journey builder', es: 'Constructor de journeys' },
    whatItDoes: {
      en: 'Builds and edits journeys — add steps and questions, import/export JSON, clone, and mark a journey as a reusable template.',
      es: 'Crea y edita journeys — agrega pasos y preguntas, importa/exporta JSON, clona, y marca una journey como plantilla reutilizable.',
    },
    whatData: {
      en: 'Journey name, steps, questions and options, plus per-question translations for multi-language journeys.',
      es: 'Nombre de la journey, pasos, preguntas y opciones, más traducciones por pregunta para journeys multi-idioma.',
    },
    whatFor: {
      en: 'To design the form a consumer fills in before the wheel spins.',
      es: 'Para diseñar el formulario que el consumidor completa antes de girar la ruleta.',
    },
    keywords: {
      en: ['journey', 'builder', 'editor', 'questions', 'import', 'export', 'template'],
      es: ['journey', 'constructor', 'editor', 'preguntas', 'importar', 'exportar', 'plantilla'],
    },
    related: ['survey-templates', 'survey-preview', 'event-surveys'],
  },
  {
    id: 'survey-templates',
    category: 'surveys',
    route: '/surveys/templates',
    title: { en: 'Journey templates', es: 'Plantillas de journey' },
    whatItDoes: {
      en: 'A library of ready-made journeys you can clone as a starting point.',
      es: 'Una biblioteca de journeys listas que puedes clonar como punto de partida.',
    },
    whatData: {
      en: 'Template journeys (some global, some from your workspace) with their steps and questions.',
      es: 'Journeys plantilla (algunas globales, otras de tu espacio) con sus pasos y preguntas.',
    },
    whatFor: {
      en: 'To avoid building a journey from scratch every time.',
      es: 'Para no construir una journey desde cero cada vez.',
    },
    keywords: {
      en: ['templates', 'journey library', 'clone', 'starter'],
      es: ['plantillas', 'biblioteca journeys', 'clonar', 'base'],
    },
    related: ['survey-builder', 'event-surveys'],
  },
  {
    id: 'survey-preview',
    category: 'surveys',
    title: { en: 'Journey preview', es: 'Vista previa de journey' },
    whatItDoes: {
      en: 'Shows a journey exactly as a consumer will see it, before attaching it to an event.',
      es: 'Muestra una journey tal como la verá el consumidor, antes de asignarla a un evento.',
    },
    whatData: {
      en: 'The rendered journey steps and questions — no responses are saved.',
      es: 'Los pasos y preguntas renderizados — no se guardan respuestas.',
    },
    whatFor: {
      en: 'To catch problems before the journey goes live at a station.',
      es: 'Para detectar problemas antes de que la journey salga en vivo en una estación.',
    },
    keywords: {
      en: ['preview', 'journey', 'test', 'check'],
      es: ['vista previa', 'journey', 'probar', 'revisar'],
    },
    related: ['survey-builder', 'public-survey-runner'],
  },
  {
    id: 'public-survey-runner',
    category: 'surveys',
    title: { en: 'Public journey (consumer view)', es: 'Journey pública (vista del consumidor)' },
    whatItDoes: {
      en: 'The anonymous experience a consumer goes through after scanning a station QR — the journey, then a prize game (spin wheel, scratch, Xinkit, pirinola, soccer, lotería or slots — whichever the brand’s plan unlocks) and the prize screen.',
      es: 'La experiencia anónima que vive el consumidor tras escanear el QR — la journey, luego un juego de premio (ruleta, raspa, Xinkit, pirinola, fútbol, lotería o tragamonedas, según lo que habilite el plan de la marca) y la pantalla de premio.',
    },
    whatData: {
      en: 'The consumer’s answers, and (for game journeys) the prize won, which decrements station stock.',
      es: 'Las respuestas del consumidor y (en journeys con juego) el premio ganado, que descuenta el stock de la estación.',
    },
    whatFor: {
      en: 'It is the end-consumer touchpoint that produces your contacts and redemptions.',
      es: 'Es el punto de contacto con el consumidor que genera tus contactos y canjes.',
    },
    keywords: {
      en: ['public journey', 'consumer', 'wheel', 'game', 'scratch', 'pirinola', 'soccer', 'loteria', 'slots', 'prize', 'runner', 'qr'],
      es: ['journey pública', 'consumidor', 'ruleta', 'juego', 'raspa', 'pirinola', 'fútbol', 'lotería', 'tragamonedas', 'premio', 'qr'],
    },
    related: ['event-surveys', 'station-qr', 'redeem', 'contacts-list'],
  },

  // ── Contacts & insights ──────────────────────────────────────────────────
  {
    id: 'contacts-list',
    category: 'contacts',
    route: '/contacts',
    title: { en: 'Contacts', es: 'Contactos' },
    whatItDoes: {
      en: 'Lists the people who responded to your journeys, deduplicated into real contacts.',
      es: 'Lista las personas que respondieron tus journeys, deduplicadas en contactos reales.',
    },
    whatData: {
      en: 'Name, email/phone when captured, visit count, prizes won, last seen and marketing consent.',
      es: 'Nombre, email/teléfono cuando se captura, número de visitas, premios ganados, última vez y consentimiento de marketing.',
    },
    whatFor: {
      en: 'It turns raw journey responses into a CRM you can export and follow up with.',
      es: 'Convierte respuestas crudas en un CRM que puedes exportar y al que dar seguimiento.',
    },
    keywords: {
      en: ['contacts', 'leads', 'crm', 'respondents', 'export', 'consent'],
      es: ['contactos', 'leads', 'crm', 'encuestados', 'exportar', 'consentimiento'],
    },
    related: ['contact-detail', 'insights', 'followup', 'integrations'],
  },
  {
    id: 'contact-detail',
    category: 'contacts',
    title: { en: 'Contact detail', es: 'Detalle de contacto' },
    whatItDoes: {
      en: 'Shows one contact’s full profile — every answer they gave and every interaction.',
      es: 'Muestra el perfil completo de un contacto — cada respuesta que dio y cada interacción.',
    },
    whatData: {
      en: 'Identity fields, answer history, prizes won and the events where they were seen.',
      es: 'Campos de identidad, historial de respuestas, premios ganados y los eventos donde fue visto.',
    },
    whatFor: {
      en: 'To understand an individual lead before reaching out.',
      es: 'Para entender a un lead individual antes de contactarlo.',
    },
    keywords: {
      en: ['contact', 'profile', 'answers', 'history', 'lead'],
      es: ['contacto', 'perfil', 'respuestas', 'historial', 'lead'],
    },
    related: ['contacts-list', 'followup'],
  },
  {
    id: 'insights',
    category: 'contacts',
    route: '/insights',
    title: { en: 'Insights', es: 'Insights' },
    whatItDoes: {
      en: 'A KPI dashboard with charts on stock distribution, redemptions, demographic breakdowns and journey responses. Scope is the brand picked in the workspace switcher.',
      es: 'Un panel de KPIs con gráficos de distribución de stock, canjes, demografía y respuestas de journey. El alcance es la marca elegida en el selector del workspace.',
    },
    whatData: {
      en: 'Aggregated metrics for the active brand, filterable by event, classification and date range.',
      es: 'Métricas agregadas de la marca activa, filtrables por evento, clasificación y rango de fechas.',
    },
    whatFor: {
      en: 'To see overall performance without opening each event one by one.',
      es: 'Para ver el rendimiento general sin abrir cada evento uno por uno.',
    },
    keywords: {
      en: ['insights', 'analytics', 'charts', 'kpi', 'dashboard'],
      es: ['insights', 'analítica', 'gráficos', 'kpi', 'panel'],
    },
    related: ['dashboard', 'trends', 'event-report', 'surveys-demographics'],
  },
  {
    id: 'photos',
    category: 'contacts',
    route: '/photos',
    title: { en: 'Photos', es: 'Fotos' },
    whatItDoes: {
      en: 'A social feed of field photos captured during activations. React (fist bump / love / fire), comment, tag photos as "recap", "great work" or "needs attention", and approve the best ones for the recap deck.',
      es: 'Un feed social de las fotos de campo capturadas durante las activaciones. Reacciona (puño / me encanta / fuego), comenta, etiqueta fotos como "recap", "gran trabajo" o "requiere atención", y aprueba las mejores para el recap.',
    },
    whatData: {
      en: 'Event photos for the active workspace (and its brands), with reaction counts, comments, tags and approval state per photo. Filterable by tag or approved-only.',
      es: 'Fotos de eventos del workspace activo (y sus marcas), con conteo de reacciones, comentarios, etiquetas y estado de aprobación por foto. Filtrable por etiqueta o solo aprobadas.',
    },
    whatFor: {
      en: 'To celebrate the field team, curate the strongest images and decide which ones make it into the client recap.',
      es: 'Para reconocer al equipo de campo, curar las mejores imágenes y decidir cuáles entran en el recap del cliente.',
    },
    keywords: {
      en: ['photos', 'feed', 'social', 'reactions', 'comments', 'tags', 'approve', 'recap'],
      es: ['fotos', 'feed', 'social', 'reacciones', 'comentarios', 'etiquetas', 'aprobar', 'recap'],
    },
    related: ['assets', 'insights', 'intake'],
  },
  {
    id: 'intake',
    category: 'events',
    route: '/intake/:token',
    title: { en: 'Collaborative intake', es: 'Intake colaborativo' },
    whatItDoes: {
      en: 'Lets you invite an outside collaborator — a venue stakeholder or a brand owner — to fill in just one scoped slice of a campaign through a passwordless magic-link. They never create an account; the link is the credential and expires automatically.',
      es: 'Permite invitar a un colaborador externo —un responsable del lugar o un dueño de marca— a completar solo una parte acotada de la campaña mediante un enlace mágico sin contraseña. Nunca crea una cuenta; el enlace es la credencial y expira automáticamente.',
    },
    whatData: {
      en: 'A scoped invite carries the target (event or brand), the invitee email/role and an expiry. The contributor only sees and edits the fields for their scope (e.g. venue address, expected attendance and budget for an event).',
      es: 'Una invitación acotada lleva el objetivo (evento o marca), el correo/rol del invitado y una expiración. El colaborador solo ve y edita los campos de su alcance (p. ej. dirección del lugar, asistencia esperada y presupuesto de un evento).',
    },
    whatFor: {
      en: 'To gather venue and brand details from people outside your team without giving them platform access or burdening them with a login.',
      es: 'Para recopilar datos del lugar y de la marca con personas ajenas a tu equipo sin darles acceso a la plataforma ni obligarlas a iniciar sesión.',
    },
    keywords: {
      en: ['intake', 'invite', 'collaborator', 'magic link', 'passwordless', 'venue', 'brand owner', 'scoped'],
      es: ['intake', 'invitación', 'colaborador', 'enlace mágico', 'sin contraseña', 'lugar', 'dueño de marca', 'acotado'],
    },
    related: ['events', 'photos'],
  },
  {
    id: 'route',
    category: 'events',
    route: '/projects/:id/route',
    title: { en: 'Route & proximity', es: 'Ruta y proximidad' },
    whatItDoes: {
      en: "Groups a project's events by calendar day and, for any day with two or more geolocated venues, draws a schematic same-day route — stop order, straight-line distances (×1.3 road factor) and estimated drive times — so field teams can plan back-to-back activations.",
      es: 'Agrupa los eventos de un proyecto por día y, para cualquier día con dos o más venues geolocalizados, dibuja una ruta esquemática del día —orden de paradas, distancias en línea recta (×1.3 factor de carretera) y tiempos de manejo estimados— para que el equipo de campo planee activaciones seguidas.',
    },
    whatData: {
      en: 'Per-day clusters of the project events that have coordinates and a start time, with per-leg miles and minutes. Distances are estimates, not turn-by-turn navigation.',
      es: 'Clústeres por día de los eventos del proyecto que tienen coordenadas y hora de inicio, con millas y minutos por tramo. Las distancias son estimaciones, no navegación giro a giro.',
    },
    whatFor: {
      en: 'To sequence same-day venues efficiently and spot when a finishing team can roll over to a nearby gap.',
      es: 'Para secuenciar venues del mismo día con eficiencia y detectar cuándo un equipo que termina puede rodar a un hueco cercano.',
    },
    keywords: {
      en: ['route', 'proximity', 'map', 'drive', 'same-day', 'venues', 'logistics', 'cluster'],
      es: ['ruta', 'proximidad', 'mapa', 'manejo', 'mismo día', 'venues', 'logística', 'clúster'],
    },
    related: ['events', 'projects'],
  },
  {
    id: 'schedule',
    category: 'events',
    route: '/schedule',
    title: { en: 'My Schedule', es: 'Mi agenda' },
    whatItDoes: {
      en: 'A cross-brand agenda of every event/shift you run, grouped by day with venue, time, staffing and status — so you can see the whole week across brands at a glance.',
      es: 'Una agenda cross-brand de cada evento/turno que gestionas, agrupada por día con lugar, hora, staffing y estado — para ver toda la semana entre marcas de un vistazo.',
    },
    whatData: {
      en: 'Events across all your brands and projects that have a date, with station and reporter counts and lifecycle status. Filterable to upcoming-only.',
      es: 'Eventos de todas tus marcas y proyectos con fecha, con conteo de stations y reporters y estado de ciclo de vida. Filtrable a solo próximos.',
    },
    whatFor: {
      en: 'To plan your week across brands and spot busy days or understaffed shifts early.',
      es: 'Para planear tu semana entre marcas y detectar días cargados o turnos sin staff a tiempo.',
    },
    keywords: {
      en: ['schedule', 'agenda', 'shifts', 'calendar', 'cross-brand', 'week'],
      es: ['agenda', 'horario', 'turnos', 'calendario', 'cross-brand', 'semana'],
    },
    related: ['events', 'projects'],
  },
  {
    id: 'surveys-demographics',
    category: 'surveys',
    title: {
      en: 'Capturing demographics in a journey',
      es: 'Capturar demografía en una journey',
    },
    whatItDoes: {
      en: 'Explains which journey components feed the Insights › Demographics panel and how to design a journey that populates them.',
      es: 'Explica qué componentes de la journey alimentan el panel Insights › Demografía y cómo armar una journey que los capture.',
    },
    whatData: {
      en: [
        'Insights › Demographics aggregates the JSON column',
        '`co_contact_interactions.demographics`, which the backend',
        'normalises from journey responses with these canonical keys:',
        '`ageRange`, `gender`, `ethnicity`, `city`, `state`, `language`.',
        '',
        'For a question to land in the demographics column, its component',
        'in the journey builder must use one of the matching keys:',
        '  · ageRange  — single-select with brackets like',
        '    13-17 / 18-24 / 25-34 / 35-44 / 45-54 / 55+',
        '  · gender    — single-select with values',
        '    female / male / non-binary / other / undisclosed',
        '  · ethnicity — single-select (e.g. hispanic / black /',
        '    white / asian / mixed / other / undisclosed)',
        '  · city      — text or location component',
        '  · state     — text or single-select component',
        '  · language  — single-select (en / es / etc.)',
        '',
        'If a journey doesn\'t include any of these components, every',
        'response gets stored with `demographics = NULL` and the panel',
        'will sit empty even after many events run.',
      ].join('\n'),
      es: [
        'Insights › Demografía agrega la columna JSON',
        '`co_contact_interactions.demographics`, que el backend',
        'normaliza desde las respuestas de la journey con estas keys',
        'canónicas: `ageRange`, `gender`, `ethnicity`, `city`, `state`, `language`.',
        '',
        'Para que una pregunta termine en la columna de demografía, su',
        'componente en el journey builder debe usar una de estas keys:',
        '  · ageRange  — selección única con rangos como',
        '    13-17 / 18-24 / 25-34 / 35-44 / 45-54 / 55+',
        '  · gender    — selección única con valores',
        '    female / male / non-binary / other / undisclosed',
        '  · ethnicity — selección única (p.ej. hispanic / black /',
        '    white / asian / mixed / other / undisclosed)',
        '  · city      — texto o componente de ubicación',
        '  · state     — texto o selección única',
        '  · language  — selección única (en / es / etc.)',
        '',
        'Si una journey no incluye ninguno de estos componentes, cada',
        'respuesta se guarda con `demographics = NULL` y el panel queda',
        'vacío aunque corran muchos eventos.',
      ].join('\n'),
    },
    whatFor: {
      en: 'So managers see real breakdowns (age, gender, location, language) instead of an empty Demographics tab.',
      es: 'Para que los gestores vean cortes reales (edad, género, ubicación, idioma) en lugar de un panel de Demografía vacío.',
    },
    keywords: {
      en: [
        'demographics',
        'age',
        'gender',
        'city',
        'state',
        'language',
        'journey design',
        'capture',
        'insights empty',
      ],
      es: [
        'demografía',
        'edad',
        'género',
        'ciudad',
        'estado',
        'idioma',
        'diseño de journey',
        'capturar',
        'insights vacío',
      ],
    },
    related: ['insights', 'survey-builder', 'survey-templates'],
  },

  // ── Operator & redemption ────────────────────────────────────────────────
  {
    id: 'redeem',
    category: 'operator',
    route: '/redeem',
    title: { en: 'Prize redemption', es: 'Canje de premios' },
    whatItDoes: {
      en: 'The booth flow to hand a prize to a winner — scan or enter the redemption code and mark it delivered.',
      es: 'El flujo del booth para entregar un premio a un ganador — escanea o ingresa el código de canje y márcalo entregado.',
    },
    whatData: {
      en: 'Redemption codes with their state (won / delivered / expired / voided) and the prize they map to.',
      es: 'Códigos de canje con su estado (ganado / entregado / expirado / anulado) y el premio al que corresponden.',
    },
    whatFor: {
      en: 'To close the prize loop — every win is tracked until the prize is in the consumer’s hand.',
      es: 'Para cerrar el ciclo del premio — cada gane se rastrea hasta que el premio está en manos del consumidor.',
    },
    keywords: {
      en: ['redeem', 'redemption', 'prize', 'scan', 'code', 'deliver'],
      es: ['canje', 'canjear', 'premio', 'escanear', 'código', 'entregar'],
    },
    related: ['operator-station', 'public-survey-runner', 'stock-list'],
  },
  {
    id: 'operator-station',
    category: 'operator',
    title: { en: 'Operator station view', es: 'Vista de operador de estación' },
    whatItDoes: {
      en: 'A mobile-first screen for the person running a booth — shows the active journey, stock left and a shortcut to the redeem scanner. Reached via an operator link minted on the station.',
      es: 'Una pantalla pensada para móvil para quien atiende un booth — muestra la journey activa, el stock restante y un atajo al escáner de canje. Se llega por un enlace de operador generado en la estación.',
    },
    whatData: {
      en: 'The station’s current journey, per-prize stock remaining and low-stock highlights.',
      es: 'La journey actual de la estación, el stock restante por premio y avisos de stock bajo.',
    },
    whatFor: {
      en: 'To let booth staff operate one station from a phone without full admin access.',
      es: 'Para que el personal del booth opere una estación desde el celular sin acceso completo de admin.',
    },
    keywords: {
      en: ['operator', 'booth', 'station', 'operator link', 'mobile'],
      es: ['operador', 'booth', 'estación', 'enlace operador', 'móvil'],
    },
    related: ['station-detail', 'redeem'],
  },

  // ── Workspace & settings ─────────────────────────────────────────────────
  {
    id: 'settings-overview',
    category: 'workspace',
    route: '/settings',
    title: { en: 'Workspace', es: 'Espacio de trabajo' },
    whatItDoes: {
      en: 'The hub for everything workspace-level — account, access, notifications, activity log, billing, follow-up and integrations.',
      es: 'El hub para todo lo del espacio de trabajo — cuenta, accesos, notificaciones, registro de actividad, facturación, seguimiento e integraciones.',
    },
    whatData: {
      en: 'A list of settings sections, each linking to its own page.',
      es: 'Una lista de secciones de configuración, cada una enlazando a su página.',
    },
    whatFor: {
      en: 'A single place to configure the workspace instead of scattered menus.',
      es: 'Un único lugar para configurar el espacio en vez de menús dispersos.',
    },
    keywords: {
      en: ['settings', 'workspace', 'configuration', 'hub'],
      es: ['ajustes', 'espacio de trabajo', 'configuración', 'hub'],
    },
    related: ['profile', 'billing', 'access-manager', 'integrations'],
  },
  {
    id: 'profile',
    category: 'workspace',
    route: '/profile',
    title: { en: 'Account & profile', es: 'Cuenta y perfil' },
    whatItDoes: {
      en: 'Edits your personal account — name, email, password, language and timezone.',
      es: 'Edita tu cuenta personal — nombre, email, contraseña, idioma y zona horaria.',
    },
    whatData: {
      en: 'Your user fields and preferences (the language choice also drives this Help center).',
      es: 'Tus campos de usuario y preferencias (la elección de idioma también rige este centro de Ayuda).',
    },
    whatFor: {
      en: 'To keep your own account details and preferences current.',
      es: 'Para mantener al día los datos y preferencias de tu propia cuenta.',
    },
    keywords: {
      en: ['profile', 'account', 'password', 'language', 'timezone'],
      es: ['perfil', 'cuenta', 'contraseña', 'idioma', 'zona horaria'],
    },
    related: ['settings-overview', 'access-manager'],
  },
  {
    id: 'access-manager',
    category: 'workspace',
    route: '/settings/access-manager',
    title: { en: 'Access manager', es: 'Gestor de accesos' },
    whatItDoes: {
      en: 'Invites teammates, assigns roles and grants access to brands, events or resources.',
      es: 'Invita compañeros, asigna roles y concede acceso a marcas, eventos o recursos.',
    },
    whatData: {
      en: 'Workspace members, their roles and per-resource grants.',
      es: 'Miembros del espacio, sus roles y permisos por recurso.',
    },
    whatFor: {
      en: 'To control who can see and change what across the workspace.',
      es: 'Para controlar quién puede ver y cambiar qué en el espacio de trabajo.',
    },
    keywords: {
      en: ['access', 'roles', 'permissions', 'team', 'invite', 'members'],
      es: ['acceso', 'roles', 'permisos', 'equipo', 'invitar', 'miembros'],
    },
    related: ['settings-overview', 'active-log', 'agency'],
  },
  {
    id: 'notifications',
    category: 'workspace',
    route: '/notifications/compose',
    title: { en: 'Notifications', es: 'Notificaciones' },
    whatItDoes: {
      en: 'Composes and sends broadcast notifications to workspace members.',
      es: 'Redacta y envía notificaciones de difusión a los miembros del espacio.',
    },
    whatData: {
      en: 'Notification message, audience and delivery status. Plan-gated — may require an upgrade.',
      es: 'Mensaje de la notificación, audiencia y estado de entrega. Depende del plan — puede requerir mejora.',
    },
    whatFor: {
      en: 'To reach your team with announcements inside the app.',
      es: 'Para llegar a tu equipo con avisos dentro de la app.',
    },
    keywords: {
      en: ['notifications', 'broadcast', 'alerts', 'announcements'],
      es: ['notificaciones', 'difusión', 'avisos', 'anuncios'],
    },
    related: ['settings-overview', 'plan-limits'],
  },
  {
    id: 'active-log',
    category: 'workspace',
    route: '/active-log',
    title: { en: 'Activity log', es: 'Registro de actividad' },
    whatItDoes: {
      en: 'An audit trail of changes made across the workspace.',
      es: 'Un rastro de auditoría de los cambios hechos en el espacio de trabajo.',
    },
    whatData: {
      en: 'Who did what and when — entity, action and user. Plan-gated.',
      es: 'Quién hizo qué y cuándo — entidad, acción y usuario. Depende del plan.',
    },
    whatFor: {
      en: 'To trace changes and answer "who changed this?".',
      es: 'Para rastrear cambios y responder "¿quién cambió esto?".',
    },
    keywords: {
      en: ['activity', 'log', 'audit', 'history', 'trail'],
      es: ['actividad', 'registro', 'auditoría', 'historial', 'rastro'],
    },
    related: ['settings-overview', 'access-manager'],
  },
  {
    id: 'followup',
    category: 'workspace',
    route: '/settings/followup',
    title: { en: 'Follow-up emails', es: 'Correos de seguimiento' },
    whatItDoes: {
      en: 'Sets up post-event follow-up emails — templates and coupons sent to contacts who opted in.',
      es: 'Configura correos de seguimiento post-evento — plantillas y cupones enviados a contactos que dieron consentimiento.',
    },
    whatData: {
      en: 'Email templates, coupon codes and dispatch stats.',
      es: 'Plantillas de correo, códigos de cupón y estadísticas de envío.',
    },
    whatFor: {
      en: 'To nurture leads automatically after an event ends.',
      es: 'Para nutrir leads automáticamente después de que termina un evento.',
    },
    keywords: {
      en: ['follow-up', 'email', 'nurture', 'coupon', 'template', 'campaign'],
      es: ['seguimiento', 'correo', 'nutrir', 'cupón', 'plantilla', 'campaña'],
    },
    related: ['contacts-list', 'integrations', 'settings-overview'],
  },
  {
    id: 'integrations',
    category: 'workspace',
    route: '/settings/integrations',
    title: { en: 'CRM integrations', es: 'Integraciones CRM' },
    whatItDoes: {
      en: 'Connects the workspace to an external CRM or webhook so new contacts flow out automatically.',
      es: 'Conecta el espacio a un CRM externo o webhook para que los contactos nuevos salgan automáticamente.',
    },
    whatData: {
      en: 'Connector configuration (HubSpot, Mailchimp, webhook), credentials and dispatch stats.',
      es: 'Configuración del conector (HubSpot, Mailchimp, webhook), credenciales y estadísticas de envío.',
    },
    whatFor: {
      en: 'To push leads into the tools your team already uses, with no manual export.',
      es: 'Para enviar leads a las herramientas que tu equipo ya usa, sin exportar manualmente.',
    },
    keywords: {
      en: ['integration', 'crm', 'webhook', 'hubspot', 'mailchimp', 'connector'],
      es: ['integración', 'crm', 'webhook', 'hubspot', 'mailchimp', 'conector'],
    },
    related: ['contacts-list', 'followup', 'settings-overview'],
  },

  // ── Billing & plan ───────────────────────────────────────────────────────
  {
    id: 'billing',
    category: 'billing',
    route: '/settings/billing',
    title: { en: 'Billing', es: 'Facturación' },
    whatItDoes: {
      en: 'Shows your subscription plan, current usage and invoices, and is where you upgrade.',
      es: 'Muestra tu plan de suscripción, el uso actual y las facturas, y es donde mejoras de plan.',
    },
    whatData: {
      en: 'Plan name, usage meters against caps, invoice history and the upgrade action.',
      es: 'Nombre del plan, medidores de uso contra los límites, historial de facturas y la acción de mejora.',
    },
    whatFor: {
      en: 'To know what your plan covers and act before limits are reached.',
      es: 'Para saber qué cubre tu plan y actuar antes de llegar a los límites.',
    },
    keywords: {
      en: ['billing', 'plan', 'subscription', 'invoices', 'upgrade', 'usage'],
      es: ['facturación', 'plan', 'suscripción', 'facturas', 'mejorar', 'uso'],
    },
    related: ['plan-limits', 'settings-overview', 'pricing'],
  },
  {
    id: 'pricing',
    category: 'billing',
    route: '/pricing',
    title: { en: 'Plans & pricing', es: 'Planes y precios' },
    whatItDoes: {
      en: 'Compares the Xamplin tiers — Free, Eventer, Eventer+, Scale and Enterprise — side by side. Planning is free forever; operations are free; the methodology (CS-1 + MENT score + ATLEDI recap) is the paid upgrade.',
      es: 'Compara los tiers de Xamplin —Free, Eventer, Eventer+, Scale y Enterprise— lado a lado. Planear es gratis para siempre; la operación es gratis; la metodología (CS-1 + score MENT + recap de ATLEDI) es el upgrade pago.',
    },
    whatData: {
      en: 'Per-tier price, brand / event-per-12-months / paid-seat / agency caps, and the feature flags each tier unlocks. Eventer+ is the most-popular tier; Enterprise is contact-only.',
      es: 'Precio por tier, límites de marcas / eventos por 12 meses / asientos pagos / agencias, y las features que desbloquea cada tier. Eventer+ es el más popular; Enterprise es solo por contacto.',
    },
    whatFor: {
      en: 'To pick the tier that matches your campaign volume and the methodology depth you need.',
      es: 'Para elegir el tier acorde a tu volumen de campañas y la profundidad de metodología que necesitas.',
    },
    keywords: {
      en: ['pricing', 'plans', 'tiers', 'eventer', 'scale', 'enterprise', 'free', 'methodology'],
      es: ['precios', 'planes', 'tiers', 'eventer', 'scale', 'enterprise', 'gratis', 'metodología'],
    },
    related: ['billing', 'plan-limits'],
  },
  {
    id: 'operations',
    category: 'workspace',
    route: '/settings/operations',
    title: { en: 'Operations', es: 'Operaciones' },
    whatItDoes: {
      en: 'Manages the operational backbone of your activations: warehouses (where your branded assets are stored) and the asset protocols your field teams follow (setup verification, condition report, return-to-warehouse, damage escalation).',
      es: 'Gestiona la columna operativa de tus activaciones: almacenes (dónde se guardan los assets de marca) y los protocolos de assets que sigue el equipo de campo (verificación de setup, reporte de condición, retorno a almacén, escalación de daños).',
    },
    whatData: {
      en: 'Warehouses (name, location, capacity) created and deleted here, scoped to your agency hierarchy, plus the reference checklist of asset protocols.',
      es: 'Almacenes (nombre, ubicación, capacidad) creados y eliminados aquí, acotados a la jerarquía de tu agencia, más el checklist de referencia de protocolos de assets.',
    },
    whatFor: {
      en: 'To keep stock logistics and field handling consistent across every event.',
      es: 'Para mantener la logística de stock y el manejo en campo consistentes en cada evento.',
    },
    keywords: {
      en: ['operations', 'warehouse', 'asset protocol', 'logistics', 'stock', 'condition'],
      es: ['operaciones', 'almacén', 'protocolo de assets', 'logística', 'stock', 'condición'],
    },
    related: ['settings-overview', 'assets'],
  },
  {
    id: 'plan-limits',
    category: 'billing',
    title: { en: 'Plan limits & grace', es: 'Límites del plan y margen de gracia' },
    whatItDoes: {
      en: 'Explains how plan caps work — alerts at 80% and 95%, a free grace window past the cap, and what gets blocked.',
      es: 'Explica cómo funcionan los límites del plan — alertas al 80% y 95%, un margen de gracia gratuito al pasar el límite, y qué se bloquea.',
    },
    whatData: {
      en: 'Usage thresholds, the grace counter and the banners shown as you approach a cap.',
      es: 'Umbrales de uso, el contador de gracia y los avisos que aparecen al acercarse a un límite.',
    },
    whatFor: {
      en: 'So a live event is never cut off unexpectedly and you know when to upgrade.',
      es: 'Para que un evento en vivo nunca se corte por sorpresa y sepas cuándo mejorar de plan.',
    },
    keywords: {
      en: ['limits', 'caps', 'grace', 'quota', 'upgrade', 'usage'],
      es: ['límites', 'topes', 'gracia', 'cuota', 'mejorar', 'uso'],
    },
    related: ['billing', 'event-live-monitor'],
  },
];
